// context/loginContext.js
import React, { createContext, useState, useEffect, useMemo } from 'react';
import { setUnauthorizedCallback } from '../config/apiClient';
import { message } from 'antd';

export const LoginContext = createContext();

const PUBLIC_ROUTES = ['/', '/login', '/jugador-acceso', '/sacar-turno', '/auth0-callback', '/registro-alumno', '/registro'];

function isValidUser(u) {
  if (!u || typeof u !== 'object') return false;
  const hasId = Number.isInteger(Number(u?.id)) || Number.isInteger(Number(u?.usu_id));
  const hasTipo = Number.isInteger(Number(u?.usu_tipousuario));
  return hasId && hasTipo;
}

export const LoginProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [hydrated, setHydrated] = useState(false);

  const redirectHomeIfNeeded = () => {
    const hash = window.location.hash;
    const hashPath = (hash.slice(1) || '').split('?')[0].trim();
    const effectiveRoute = hashPath ? `/${hashPath}` : '/';
    const isPublicRoute = effectiveRoute === '/' ||
                          effectiveRoute === '/login' ||
                          effectiveRoute === '/jugador-acceso' ||
                          effectiveRoute === '/sacar-turno' ||
                          effectiveRoute.startsWith('/auth0-callback') ||
                          effectiveRoute.startsWith('/registro') ||
                          effectiveRoute === '/registro-alumno';
    if (!isPublicRoute) {
      window.location.href = '/#/';
    }
  };

  const hardLogout = (reason) => {
    console.log('[logout] cerrando sesión ->', reason);
    setUser(null);
    try { localStorage.removeItem('user'); } catch { }
    sessionStorage.setItem('justLoggedOut', 'true');
    sessionStorage.removeItem('sesionExpirada');
    redirectHomeIfNeeded();
  };

  const checkUserFromStorage = () => {
    let stored = null;
    try {
      const raw = localStorage.getItem('user');
      if (!raw) return null;
      stored = JSON.parse(raw);
    } catch {
      return null;
    }
    return stored && isValidUser(stored) ? stored : null;
  };

  // 1) Hidratar desde storage + autocierre si está mal
  useEffect(() => {
    const path = window.location.pathname;
    const hash = window.location.hash;
    
    // Verificar si hay un código de Auth0 en la URL (estamos procesando el callback)
    const urlParams = new URLSearchParams(window.location.search);
    const hashParams = new URLSearchParams(hash.split('?')[1] || '');
    const hasAuth0Code = urlParams.get('code') || hashParams.get('code');
    
    // Con HashRouter la ruta real está en el hash; el pathname del navegador suele ser siempre "/".
    // Si consideramos solo path === '/' como público, nunca cargaríamos el usuario en /#/jugador/mis-turnos.
    const hashPath = (hash.slice(1) || '').split('?')[0].trim();
    const effectiveRoute = hashPath ? `/${hashPath}` : '/';
    const isPublicRoute = effectiveRoute === '/' ||
                          effectiveRoute === '/login' ||
                          effectiveRoute === '/jugador-acceso' ||
                          effectiveRoute === '/sacar-turno' ||
                          effectiveRoute.startsWith('/auth0-callback') ||
                          effectiveRoute.startsWith('/registro') ||
                          effectiveRoute === '/registro-alumno' ||
                          hasAuth0Code;
    
    console.log('🔍 [LoginContext] Hidratación:', {
      path,
      hash,
      effectiveRoute,
      isPublicRoute,
      hasAuth0Code,
      justLoggedIn: sessionStorage.getItem('justLoggedIn'),
    });
    
    // No hacer logout si estamos en una ruta pública O si estamos procesando el callback de Auth0
    if (isPublicRoute || hasAuth0Code) {
      console.log('✅ [LoginContext] Ruta pública o callback de Auth0, no hacer logout');
      setHydrated(true);
      return;
    }
    
    // Si acabamos de hacer login (hay un flag en sessionStorage), no hacer logout
    const justLoggedIn = sessionStorage.getItem('justLoggedIn');
    if (justLoggedIn) {
      console.log('✅ [LoginContext] justLoggedIn flag encontrado, no hacer logout');
      sessionStorage.removeItem('justLoggedIn');
      const storedUser = checkUserFromStorage();
      if (storedUser) {
        setUser(storedUser);
        setHydrated(true);
        return;
      }
    }
    
    const storedUser = checkUserFromStorage();
    if (storedUser) {
      console.log('✅ [LoginContext] Usuario encontrado en storage');
      setUser(storedUser);
    } else {
      console.log('❌ [LoginContext] No hay usuario en storage, haciendo logout');
      hardLogout('sin usuario válido en storage');
    }
    setHydrated(true);
  }, []);

  // 2) Preparar callback global para 401/403
  useEffect(() => {
    setUnauthorizedCallback(() => {
      hardLogout('sesión expirada (401/403)');
      sessionStorage.setItem('sesionExpirada', 'true');
      message.error('Sesión expirada. Redirigiendo al inicio…');
    });
  }, []);

  // 3) Sincronizar entre pestañas/ventanas
  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === 'user') {
        const stored = checkUserFromStorage();
        if (!stored) {
          hardLogout('storage user removido/ inválido (otra pestaña)');
        } else {
          setUser(stored);
        }
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  // 4) Revalidar cuando la pestaña vuelve al frente
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        // No hacer logout si acabamos de hacer login o si estamos en una ruta pública
        const justLoggedIn = sessionStorage.getItem('justLoggedIn');
        const justLoggedOut = sessionStorage.getItem('justLoggedOut');
        const sesionExpirada = sessionStorage.getItem('sesionExpirada');
        
        const hash = window.location.hash;
        const hashPath = (hash.slice(1) || '').split('?')[0].trim();
        const effectiveRoute = hashPath ? `/${hashPath}` : '/';
        const isPublicRoute = effectiveRoute === '/' ||
                              effectiveRoute === '/login' ||
                              effectiveRoute === '/jugador-acceso' ||
                              effectiveRoute === '/sacar-turno' ||
                              effectiveRoute.startsWith('/auth0-callback') ||
                              effectiveRoute.startsWith('/registro') ||
                              effectiveRoute === '/registro-alumno';
        const urlParams = new URLSearchParams(window.location.search);
        const hashParams = new URLSearchParams(hash.split('?')[1] || '');
        const hasAuth0Code = urlParams.get('code') || hashParams.get('code');
        
        if (justLoggedIn || isPublicRoute || hasAuth0Code) {
          console.log('[onVisible] Saltando logout - justLoggedIn, ruta pública o callback de Auth0');
          return;
        }
        
        // Si acabamos de hacer logout o la sesión expiró, no hacer nada
        if (justLoggedOut || sesionExpirada) {
          console.log('[onVisible] Saltando logout - justLoggedOut o sesión expirada');
          return;
        }
        
        const stored = checkUserFromStorage();
        if (!stored) {
          hardLogout('revalidación al volver a la pestaña');
        } else {
          setUser(stored);
        }
      }
    };
    document.addEventListener('visibilitychange', onVisible);
    return () => document.removeEventListener('visibilitychange', onVisible);
  }, []);

  const login = async (userData) => {
    console.log('🔐 Validando usuario:', userData);
    console.log('🔐 isValidUser result:', isValidUser(userData));
    
    if (!isValidUser(userData)) {
      console.error('❌ Usuario inválido:', userData);
      message.error('Error al iniciar sesión: datos de usuario inválidos.');
      return;
    }

    // Si el backend devolvió refreshToken, guardarlo en localStorage
    if (userData.refreshToken) {
      try {
        localStorage.setItem('refreshToken', userData.refreshToken);
      } catch (err) {
        console.warn('No se pudo guardar refresh token:', err);
      }
    }

    setUser(userData);
    try { 
      localStorage.setItem('user', JSON.stringify(userData));
      console.log('✅ Usuario guardado en localStorage');
    } catch (err) {
      console.error('❌ Error al guardar usuario:', err);
    }
  };

  const logout = () => {
    hardLogout('logout manual');
  };

  const isAuthenticated = !!user;

  const value = useMemo(
    () => ({ user, login, logout, isAuthenticated, hydrated }),
    [user, isAuthenticated, hydrated]
  );

  return (
    <LoginContext.Provider value={value}>
      {hydrated ? children : null}
    </LoginContext.Provider>
  );
};

